import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiLayers, FiTrendingUp, FiShield, FiArrowLeft, FiArrowRight, FiX } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

// Import translations
import fa from "./locales/SocialImpact-fa.json";
import en from "./locales/SocialImpact-en.json";
import ar from "./locales/SocialImpact-ar.json";
import tr from "./locales/SocialImpact-tr.json";

const SocialImpact: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "SocialImpact";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [activeCategory, setActiveCategory] = useState<string>("all");
    const [selectedCard, setSelectedCard] = useState<number | null>(null);
    const [showAllCards, setShowAllCards] = useState<boolean>(false);
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";

    const categories = Array.from({ length: 5 }, (_, i) => {
        const cat = t(`categories.${i}`, { ns: "SocialImpact", returnObjects: true, defaultValue: null }) as any;
        if (cat && cat.id) return cat;
        return null;
    }).filter(Boolean) as Array<{ id: string; name: string }>;

    const initiatives = Array.from({ length: 12 }, (_, i) => {
        const init = t(`initiatives.${i}`, { ns: "SocialImpact", returnObjects: true, defaultValue: null }) as any;
        if (init && init.title) return init;
        return null;
    }).filter(Boolean) as Array<{
        title: string;
        description: string;
        image: string;
        category: string;
        actions: string[];
        color: string;
    }>;

    const filteredInitiatives = activeCategory === "all" ? initiatives : initiatives.filter((initiative) => initiative.category === activeCategory);
    const visibleInitiatives = showAllCards ? filteredInitiatives : filteredInitiatives.slice(0, 3);

    const getCategoryName = (catId: string) => {
        const cat = categories.find((c) => c.id === catId);
        return cat ? cat.name : catId;
    };

    const closeDetails = () => setSelectedCard(null);

    const scrollToContact = () => {
        closeDetails();
        const element = document.getElementById("contact");
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <section ref={sectionRef} dir={dir} id="social-impact" className="pb-32 relative overflow-hidden">
            <div className="container mx-auto relative z-10">
                <SectionHeader
                    regularText={t("sectionHeader.regularText", { ns: "SocialImpact" })}
                    highlightedText={t("sectionHeader.highlightedText", { ns: "SocialImpact" })}
                    descriptionText={t("sectionHeader.descriptionText", { ns: "SocialImpact" })}
                    descriptionHighlight={t("sectionHeader.descriptionHighlight", { ns: "SocialImpact" })}
                    descriptionExtra={<span>{t("sectionHeader.descriptionExtra", { ns: "SocialImpact" })}</span>}
                    className="mb-16"
                />

                {/* Categories Navigation */}
                <div className="relative w-full md:w-10/12 mx-auto overflow-x-auto py-2 no-scrollbar mb-8">
                    <div className="flex gap-4 min-w-max px-2 justify-center items-center">
                        <motion.div
                            className="flex flex-wrap justify-center gap-2.5"
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            {categories.map((category, index) => (
                                <motion.button
                                    key={category.id}
                                    className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                                        activeCategory === category.id
                                            ? "bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] text-white shadow-md shadow-blue-500/20"
                                            : "bg-white border border-slate-200/80 text-slate-600 hover:bg-blue-50/60 hover:text-[#0072CE] hover:border-blue-200"
                                    }`}
                                    onClick={() => setActiveCategory(category.id)}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                                >
                                    {category.name}
                                </motion.button>
                            ))}
                        </motion.div>
                    </div>
                </div>

                {/* Initiatives Grid */}
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <AnimatePresence mode="popLayout">
                        {visibleInitiatives.map((initiative, index) => (
                            <motion.div
                                key={`${initiative.title}-${index}`}
                                className="group relative bg-white/90 backdrop-blur-sm rounded-2xl border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 overflow-hidden flex flex-col justify-between cursor-pointer"
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                whileHover={{ y: -4 }}
                                onClick={() => setSelectedCard(index)}
                            >
                                {/* Image Container */}
                                <div className="h-48 overflow-hidden relative">
                                    <img
                                        src={initiative.image}
                                        alt={initiative.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/10 to-transparent"></div>
                                    <div className="absolute top-3 end-3">
                                        <span className="bg-white/95 backdrop-blur-md text-[11px] font-bold text-[#0072CE] px-3 py-1 rounded-full shadow-xs border border-blue-100">
                                            {getCategoryName(initiative.category)}
                                        </span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-5 sm:p-6 flex-1 flex flex-col justify-between">
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-800 group-hover:text-[#0072CE] transition-colors leading-snug mb-2">
                                            {initiative.title}
                                        </h3>
                                        <p className="text-slate-600 text-xs sm:text-sm line-clamp-3 leading-relaxed mb-4">
                                            {initiative.description}
                                        </p>

                                        {/* Preview of key actions */}
                                        {initiative.actions && initiative.actions.length > 0 && (
                                            <div className="space-y-1.5 mb-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                                                {initiative.actions.slice(0, 2).map((action, aIdx) => (
                                                    <div key={aIdx} className="flex items-start gap-2 text-xs text-slate-700">
                                                        <FiCheckCircle className="w-3.5 h-3.5 text-[#0072CE] shrink-0 mt-0.5" />
                                                        <span className="line-clamp-1">{action}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer Link */}
                                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#0072CE] group-hover:text-[#0038A8] transition-colors">
                                        <span>{isRTL ? "مشاهده راهکار و جزئیات" : "View Details & Solutions"}</span>
                                        {isRTL ? (
                                            <FiArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                        ) : (
                                            <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </motion.div>

                {/* Show More Button */}
                {filteredInitiatives.length > 3 && (
                    <motion.div className="flex justify-center mt-10" initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.6 }}>
                        <motion.button
                            className="px-6 py-3 font-semibold text-xs sm:text-sm rounded-full bg-white text-[#0072CE] border border-blue-200/80 hover:bg-blue-50/80 hover:border-blue-300 transition-all duration-300 flex items-center gap-2 shadow-xs"
                            onClick={() => setShowAllCards(!showAllCards)}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                        >
                            <span>{showAllCards ? t("labels.showLess", { ns: "SocialImpact" }) : t("labels.showMore", { ns: "SocialImpact" })}</span>
                            <motion.svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                animate={{ rotate: showAllCards ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </motion.svg>
                        </motion.button>
                    </motion.div>
                )}
            </div>

            {/* Structured Card Modal */}
            <AnimatePresence>
                {selectedCard !== null && visibleInitiatives[selectedCard] && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto"
                        onClick={closeDetails}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 15 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 15 }}
                            transition={{ type: "spring", duration: 0.4 }}
                            className="relative max-w-2xl w-full max-h-[88vh] bg-white backdrop-blur-2xl p-0 rounded-3xl shadow-2xl border border-slate-100 overflow-y-auto my-auto scrollbar-thin scrollbar-thumb-slate-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Top Gradient Accent Line */}
                            <div className="h-1.5 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] w-full sticky top-0 z-30"></div>

                            {/* Close Button */}
                            <button
                                onClick={closeDetails}
                                className="absolute top-4 end-4 w-8 h-8 rounded-full bg-slate-900/50 hover:bg-slate-900/80 text-white backdrop-blur-md flex items-center justify-center text-sm transition-colors z-30"
                                aria-label="Close"
                            >
                                <FiX className="w-4 h-4" />
                            </button>

                            {/* Header Image Banner */}
                            <div className="relative h-48 sm:h-56 w-full overflow-hidden">
                                <img
                                    src={visibleInitiatives[selectedCard].image}
                                    alt={visibleInitiatives[selectedCard].title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent"></div>
                                <div className="absolute bottom-4 start-6">
                                    <span className="bg-white/95 backdrop-blur-md text-xs font-bold text-[#0072CE] px-3.5 py-1.5 rounded-full shadow-md">
                                        {getCategoryName(visibleInitiatives[selectedCard].category)}
                                    </span>
                                </div>
                            </div>

                            {/* Modal Content Details */}
                            <div className="p-6 sm:p-8 space-y-6">
                                <div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">
                                        {visibleInitiatives[selectedCard].title}
                                    </h3>

                                    {/* Section 1: Overview & Problem Context */}
                                    <div className="bg-gradient-to-br from-blue-50/70 to-slate-50 p-4 sm:p-5 rounded-2xl border border-blue-100/80">
                                        <div className="flex items-center gap-2 text-[#0072CE] font-bold text-xs uppercase tracking-wider mb-2">
                                            <FiLayers className="w-4 h-4" />
                                            <span>{isRTL ? "شرح و هدف طرح اجتماعی" : "Initiative Overview"}</span>
                                        </div>
                                        <p className="text-slate-700 text-xs sm:text-sm leading-relaxed">
                                            {visibleInitiatives[selectedCard].description}
                                        </p>
                                    </div>
                                </div>

                                {/* Section 2: Key Solutions Grid */}
                                <div>
                                    <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                                        <FiCheckCircle className="w-4 h-4 text-[#0072CE]" />
                                        <span>{isRTL ? "راهکارهای هوشمند و اقدامات کلیدی دلبند" : "Smart Delband Solutions"}</span>
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {visibleInitiatives[selectedCard].actions.map((action, actionIndex) => (
                                            <div
                                                key={actionIndex}
                                                className="flex items-start gap-2.5 p-3.5 rounded-xl bg-slate-50 border border-slate-100 text-xs sm:text-sm text-slate-700 font-medium"
                                            >
                                                <FiCheckCircle className="w-4 h-4 text-[#0072CE] shrink-0 mt-0.5" />
                                                <span>{action}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Section 3: Value & Impact Overview */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-blue-100 text-[#0072CE] flex items-center justify-center shrink-0">
                                            <FiTrendingUp className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h5 className="text-xs font-bold text-slate-800">{isRTL ? "ارزش اجتماعی" : "Social Value"}</h5>
                                            <p className="text-[11px] text-slate-500 mt-0.5">{isRTL ? "ارتقای سلامت عمومی و کاهش هزینه‌های درمان" : "Enhanced community health & care access"}</p>
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-blue-100 text-[#0072CE] flex items-center justify-center shrink-0">
                                            <FiShield className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h5 className="text-xs font-bold text-slate-800">{isRTL ? "پایداری و ایمنی مراقبت" : "Care Reliability"}</h5>
                                            <p className="text-[11px] text-slate-500 mt-0.5">{isRTL ? "پایش مستمر و پاسخگویی سریع در شرایط اضطراری" : "Continuous remote monitoring & safety"}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-slate-100">
                                    <motion.a
                                        href="https://api.whatsapp.com/message/GNNOKQBPQVSQB1?autoload=1&app_absent=0"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-gradient-to-r from-[#25D366] via-[#128C7E] to-[#075E54] text-white py-3.5 px-6 rounded-xl font-semibold text-sm shadow-md shadow-green-500/20 hover:shadow-lg hover:shadow-green-500/30 transition-all text-center flex items-center justify-center gap-2 cursor-pointer"
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={closeDetails}
                                    >
                                        <FaWhatsapp className="w-5 h-5 text-white shrink-0" />
                                        <span>{isRTL ? "ارتباط و مشاوره در واتس‌اپ" : "Consultation on WhatsApp"}</span>
                                        {isRTL ? <FiArrowLeft className="w-4 h-4" /> : <FiArrowRight className="w-4 h-4" />}
                                    </motion.a>
                                    <button
                                        className="px-5 py-3.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold transition-colors cursor-pointer"
                                        onClick={closeDetails}
                                    >
                                        {isRTL ? "بستن" : "Close"}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default SocialImpact;

